import { useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useAds } from '../../useAds';

const injectCustomButtonStyles = (iframe: HTMLIFrameElement) => {
  try {
    const iframeDocument = iframe.contentDocument;
    if (!iframeDocument) return;
    const styleSheet = iframeDocument.createElement('style');
    styleSheet.textContent = `
      #creative {
        box-sizing: border-box !important;
        position: relative !important;
        border-width: 0 8px 8px 8px !important;
        border-style: solid !important;
        border-color: #F6F7FA !important;
        border-radius: 8px !important;
        overflow: hidden !important;
      }
      #creative::before {
        content: 'PUBLICIDADE';
        display: flex;
        justify-content: center;
        width: 100%;
        padding: 4px 0;
        background-color: #F6F7FA;
        font-family: "Source Sans 3", sans-serif;
        font-size: 12px;
        font-weight: 600;
        text-align: center;
        box-sizing: border-box;
      }
      .continue-prompt-text { display: none !important; }
      .close-button-outer {
        &::after {
          content: 'Fechar Anúncio';
          font-family: 'Lexend Deca', sans-serif;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
        }
        margin: 24px;
        background-color: #0048db;
        height: 35px;
        border-radius: 48px;
        padding: 0 18px;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border: none;
        cursor: pointer;
      }
    `;
    iframeDocument.head.appendChild(styleSheet);
  } catch (e) {
    console.error('Failed to inject styles into the ad iframe.', e);
  }
};

interface InterstitialSlotProps {
  path: string;
  targetings?: { key: string; value: string }[];
  autoCloseAfterSeconds?: number;
}

export interface InterstitialSlotRef {
  show: () => void;
}

const InterstitialSlotComponent = forwardRef<InterstitialSlotRef, InterstitialSlotProps>(({ path, targetings, autoCloseAfterSeconds }, ref) => {
  const { createInterstitialSlot, destroySlot } = useAds();

  const observerRef = useRef<MutationObserver | null>(null);
  const slotRef = useRef<googletag.Slot | null>(null);

  useEffect(() => {
    let adPath = path;
    if (import.meta.env.VITE_ENV === 'dev') {
      adPath = '/6355419/Travel/Europe/France/Paris';
    }

    const slot = createInterstitialSlot(adPath, targetings);
    if (slot) {
      slotRef.current = slot;
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      if (slotRef.current) destroySlot(slotRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    show: () => {
      const currentSlot = slotRef.current;
      if (!currentSlot) {
        console.warn('The interstitial slot has already been destroyed or is not available.');
        return;
      }

      window.googletag.cmd.push(() => {
        googletag.pubads().addEventListener('slotRenderEnded', function onSlotRendered(event) {
          if (event.slot === currentSlot && !event.isEmpty) {
            setTimeout(() => {
              try {
                const insElement = document.getElementById(currentSlot.getSlotElementId());
                if (insElement) {
                  // Apply custom styles to the interstitial container for better visibility
                  insElement.style.setProperty('background', 'rgba(22, 24, 29, 0.7)', 'important');
                  insElement.style.setProperty('display', 'flex', 'important');
                  insElement.style.setProperty('justify-content', 'center', 'important');
                  insElement.style.setProperty('align-items', 'center', 'important');
                  insElement.style.setProperty('position', 'fixed', 'important');

                  const adIframe = insElement.querySelector('iframe');
                  if (adIframe) injectCustomButtonStyles(adIframe);

                  // Auto close the ad after a specified time, if provided
                  if (autoCloseAfterSeconds && autoCloseAfterSeconds > 0) {
                    setTimeout(() => {
                      insElement.style.setProperty('display', 'none', 'important');
                      destroySlot(currentSlot);
                      slotRef.current = null;
                    }, autoCloseAfterSeconds * 1000);
                  }

                  // Observe style changes to maintain fixed positioning
                  const observer = new MutationObserver((mutationsList) => {
                    for (const mutation of mutationsList) {
                      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                        const targetElement = mutation.target as HTMLElement;
                        if (targetElement.style.position !== 'fixed') {
                          targetElement.style.setProperty('position', 'fixed', 'important');
                        }
                      }
                    }
                  });
                  observer.observe(insElement, { attributes: true });
                  observerRef.current = observer;
                }
              } catch (e) {
                console.error('Error trying to fix the interstitial CSS:', e);
              }
            }, 0);
            googletag.pubads().removeEventListener('slotRenderEnded', onSlotRendered);
          }
        });
      });

      window.googletag.cmd.push(() => {
        googletag.display(currentSlot);
      });
    },
  }));

  return null;
});

export const InterstitialSlot = InterstitialSlotComponent;
